import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSession } from '@/lib/auth';
import { listMySubscriptions, listSubscriptionPlans } from '@/lib/api/core';

/**
 * useMySubscriptionHistory — historial de suscripciones del usuario
 * autenticado vía `GET /v1/subscriptions/history` (api-core).
 *
 * Hacemos join in-memory con `GET /v1/subscriptions/plans` para resolver el
 * nombre del plan + monto (precio en centavos → pesos enteros). El endpoint
 * de planes no desglosa monthly/annual (GAP-04), así que el monto mostrado
 * es el precio base del plan.
 */

export type SubscriptionHistoryItem = {
  id: string;
  planId: string;
  planName: string;
  planSlug: string;
  cycle: 'monthly' | 'annual' | string;
  startedAt: string | null;
  endsAt: string | null;
  status: string;
  /** Precio del plan en el ciclo correspondiente (pesos enteros COP).
   *  Null si el join con `subscription_plans` no resolvió. */
  amountCop: number | null;
  createdAt: string | null;
};

function centsToWholeCop(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(n) ? Math.round(n / 100) : null;
}

async function fetchMySubscriptionHistory(_userId: string): Promise<SubscriptionHistoryItem[]> {
  void _userId; // el endpoint filtra por el usuario del JWT server-side.
  const [history, plans] = await Promise.all([listMySubscriptions(), listSubscriptionPlans()]);

  const planMeta = new Map<string, { name: string; slug: string; priceCop: number | null }>();
  for (const p of plans) {
    const displayName =
      typeof p.display_name === 'string' && p.display_name.trim()
        ? p.display_name.trim()
        : typeof p.name === 'string' && p.name.trim()
          ? p.name.trim()
          : 'Plan';
    planMeta.set(String(p.id), {
      name: displayName,
      slug: typeof p.slug === 'string' ? p.slug : '',
      priceCop: centsToWholeCop(p.price),
    });
  }

  return history.map((r) => {
    const planId = String(r.plan_id ?? '');
    const cycleRaw = r.cycle === 'monthly' || r.cycle === 'annual' ? r.cycle : 'monthly';
    const plan = planMeta.get(planId);
    return {
      id: String(r.id),
      planId,
      planName: plan?.name ?? 'Plan',
      planSlug: plan?.slug ?? '',
      cycle: cycleRaw,
      startedAt: (r.started_at as string | null) ?? null,
      endsAt: (r.ends_at as string | null) ?? null,
      status: typeof r.status === 'string' ? r.status : 'unknown',
      amountCop: plan?.priceCop ?? null,
      createdAt: (r.created_at as string | null) ?? null,
    };
  });
}

export function useMySubscriptionHistory(): UseQueryResult<SubscriptionHistoryItem[]> {
  const { user } = useSession();
  const userId = user?.id;

  return useQuery({
    queryKey: ['my-subscription-history', userId ?? 'anon'],
    queryFn: () => fetchMySubscriptionHistory(userId as string),
    enabled: !!userId,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
